import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchUserSystemExtraPermissions,
    updateUserSystemExtraPermissions,
} from "model/UserModel";
import "./extra-permissions-add-edit-modal.scss";

const normalizePermission = (permission = {}) => ({
    id: String(permission?.id ?? "").trim(),
    code: String(permission?.code ?? "").trim(),
    module: String(permission?.module ?? "Other").trim() || "Other",
    description: String(permission?.description ?? permission?.code ?? "").trim(),
    isAssigned: Boolean(
        permission?.isAssigned ??
            permission?.isSelected ??
            permission?.selected ??
            permission?.assigned
    ),
});

const resolveAssignedPermissionIds = (data = {}, available = []) => {
    const assignedFromRoot = [
        ...(Array.isArray(data?.permissionIds) ? data.permissionIds : []),
        ...(Array.isArray(data?.assignedPermissionIds) ? data.assignedPermissionIds : []),
        ...(Array.isArray(data?.assigned) ? data.assigned : []),
        ...(Array.isArray(data?.selected) ? data.selected : []),
    ]
        .map((id) => String(id ?? "").trim())
        .filter(Boolean);

    if (assignedFromRoot.length) {
        return Array.from(new Set(assignedFromRoot));
    }

    return available
        .filter((permission) => permission?.isAssigned)
        .map((permission) => permission.id)
        .filter(Boolean);
};

const buildPermissionGroups = (permissions = []) => {
    const groupMap = new Map();

    (Array.isArray(permissions) ? permissions : []).forEach((permission) => {
        const moduleName = permission?.module || "Other";
        if (!groupMap.has(moduleName)) {
            groupMap.set(moduleName, []);
        }
        groupMap.get(moduleName).push(permission);
    });

    return Array.from(groupMap.entries())
        .sort(([leftModule], [rightModule]) =>
            String(leftModule).localeCompare(String(rightModule))
        )
        .map(([module, items]) => ({
            module,
            items: [...items].sort((left, right) =>
                String(left?.description || "").localeCompare(
                    String(right?.description || "")
                )
            ),
        }));
};

const buildSelectionKeyName = (selectedIds = []) =>
    [...selectedIds].map(String).sort().join("|");

const ExtraPermissionsAddEditModal = ({
    extraPermissionsModalOpen,
    setExtraPermissionsModalOpen,
    userRow = null,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [userId, setUserId] = useState("");
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [permissions, setPermissions] = useState([]);
    const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
    const [selectionKeyName, setSelectionKeyName] = useState("");
    const [searchText, setSearchText] = useState("");

    const resolvedUserId = String(
        userRow?.id ?? userRow?._id ?? userRow?.userId ?? userId ?? ""
    ).trim();

    const filteredPermissions = useMemo(() => {
        const query = String(searchText || "").trim().toLowerCase();
        if (!query) return permissions || [];

        return (permissions || []).filter((permission) => {
            const haystack = [
                permission?.description,
                permission?.code,
                permission?.module,
            ]
                .map((value) => String(value || "").toLowerCase())
                .join(" ");
            return haystack.includes(query);
        });
    }, [permissions, searchText]);

    const permissionGroups = useMemo(
        () => buildPermissionGroups(filteredPermissions),
        [filteredPermissions]
    );

    const assignedSelectionKeyName = useMemo(
        () => buildSelectionKeyName(selectedPermissionIds),
        [selectedPermissionIds]
    );

    const selectedPermissionIdSet = useMemo(
        () => new Set(selectedPermissionIds.map(String)),
        [selectedPermissionIds]
    );

    const selectedCount = selectedPermissionIds.length;
    const totalCount = permissions.length;

    useEffect(() => {
        if (!extraPermissionsModalOpen) return;

        const rowId = String(
            userRow?.id ?? userRow?._id ?? userRow?.userId ?? ""
        ).trim();
        if (!rowId) {
            toast.error("User id is missing");
            return;
        }

        let cancelled = false;

        const loadExtraPermissions = async () => {
            setLoading(true);
            setSearchText("");
            try {
                const response = await fetchUserSystemExtraPermissions(rowId);
                if (cancelled) return;

                const data = response?.data ?? {};
                const availableRows = Array.isArray(data?.available) ? data.available : [];
                const normalizedPermissions = availableRows
                    .map(normalizePermission)
                    .filter((permission) => permission.id);
                const assignedIds = resolveAssignedPermissionIds(
                    data,
                    normalizedPermissions
                );

                setUserId(String(data?.userId ?? rowId).trim());
                setUserName(
                    String(
                        data?.userName ??
                            userRow?.name ??
                            userRow?.userName ??
                            ""
                    ).trim()
                );
                setUserEmail(String(userRow?.email ?? "").trim());
                setPermissions(normalizedPermissions);
                setSelectedPermissionIds(assignedIds);
                setSelectionKeyName(buildSelectionKeyName(assignedIds));
            } catch {
                if (!cancelled) {
                    setPermissions([]);
                    setSelectedPermissionIds([]);
                    setSelectionKeyName("");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadExtraPermissions();

        return () => {
            cancelled = true;
        };
    }, [extraPermissionsModalOpen, userRow]);

    const closeModal = () => {
        setExtraPermissionsModalOpen(false);
    };

    const handlePermissionToggle = (permissionId, checked) => {
        const normalizedId = String(permissionId ?? "").trim();
        if (!normalizedId) return;

        setSelectedPermissionIds((prev) => {
            const next = new Set((prev || []).map(String));
            if (checked) {
                next.add(normalizedId);
            } else {
                next.delete(normalizedId);
            }
            return Array.from(next);
        });
    };

    const handleSave = async () => {
        if (!resolvedUserId) {
            toast.error("User id is missing");
            return;
        }

        setSaving(true);
        try {
            const response = await updateUserSystemExtraPermissions(resolvedUserId, {
                userId: resolvedUserId,
                permissionIds: selectedPermissionIds.map(String),
            });

            if (response?.success) {
                toast.success(response?.message || "Permissions updated successfully");
                closeModal();
                return;
            }

            toast.error(response?.message || "Unable to update permissions");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={extraPermissionsModalOpen}
            toggle={closeModal}
            backdrop="static"
            size="lg"
            className="modal-full-height extra-permissions-add-edit-modal"
            centered
        >
            <ModalBody className="extra-permissions-modal-body">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold m-0 text-uppercase">Permission</h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr className="mt-3 mb-3" />

                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <>
                        <div className="extra-permissions-user-card">
                            <div className="extra-permissions-user-field">
                                <div className="extra-permissions-user-field__label">
                                    <i className="fa fa-envelope-o" aria-hidden="true" />
                                    Email
                                </div>
                                <div className="extra-permissions-user-field__value">
                                    {userEmail || "—"}
                                </div>
                            </div>
                            <div className="extra-permissions-user-field">
                                <div className="extra-permissions-user-field__label">
                                    <i className="fa fa-user" aria-hidden="true" />
                                    Name
                                </div>
                                <div className="extra-permissions-user-field__value">
                                    {userName || "—"}
                                </div>
                            </div>
                        </div>

                        {/* <div className="extra-permissions-toolbar">
                            <span className="extra-permissions-summary">
                                <i className="fa fa-check-square-o" aria-hidden="true" />
                                {selectedCount} of {totalCount} selected
                            </span>
                            <div className="extra-permissions-search-wrap">
                                <i className="fa fa-search" aria-hidden="true" />
                                <Input
                                    type="text"
                                    className="extra-permissions-search-input"
                                    placeholder="Search permissions..."
                                    value={searchText}
                                    onChange={(event) =>
                                        setSearchText(event?.target?.value || "")
                                    }
                                />
                            </div>
                        </div> */}

                        <div className="extra-permissions-scroll">
                            {permissionGroups.length ? (
                                permissionGroups.map((group) => (
                                    <div
                                        key={`permission-module-${group.module}`}
                                        className="extra-permissions-module"
                                    >
                                        <div className="extra-permissions-module__title">
                                            {group.module}
                                        </div>
                                        <div className="extra-permissions-grid">
                                            {(group.items || []).map((permission) => {
                                                const permissionId = permission.id;
                                                const isSelected =
                                                    selectedPermissionIdSet.has(permissionId);

                                                return (
                                                    <label
                                                        key={`permission-item-${permissionId}`}
                                                        className={`extra-permissions-item ${
                                                            isSelected ? "is-selected" : ""
                                                        }`}
                                                        htmlFor={`extra-permission-${permissionId}`}
                                                    >
                                                        <Input
                                                            key={`extra-permission-${permissionId}-${assignedSelectionKeyName}-${selectionKeyName}`}
                                                            id={`extra-permission-${permissionId}`}
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            defaultChecked={isSelected}
                                                            onChange={(event) =>
                                                                handlePermissionToggle(
                                                                    permissionId,
                                                                    event.target.checked
                                                                )
                                                            }
                                                        />
                                                        <span className="extra-permissions-item__label">
                                                            {permission.description ||
                                                                permission.code}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="extra-permissions-empty">
                                    {searchText
                                        ? "No permissions match your search."
                                        : "No permissions found."}
                                </div>
                            )}
                        </div>

                        <div className="extra-permissions-footer">
                            <Button color="primary" onClick={handleSave} disabled={saving}>
                                {saving ? <Spinner size="sm" className="me-1" /> : null}
                                Save
                            </Button>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

ExtraPermissionsAddEditModal.propTypes = {
    extraPermissionsModalOpen: PropTypes.bool,
    setExtraPermissionsModalOpen: PropTypes.func,
    userRow: PropTypes.object,
};

export default ExtraPermissionsAddEditModal;
